<script setup lang="ts">
import { computed, onMounted, ref, watch, defineProps, type PropType, onBeforeUnmount } from 'vue';
import { useGlobal, useMap, usePois, useImageDownloader } from '@/composables';
import { clamp } from '@/lib/utils';
import type { FirstAnimationFrame, ZoneAnimation } from '@/types';

const { isDragging } = useGlobal();
const { isHighQuality, isRgb } = useMap();
const { poiFirstsFrames } = usePois();
const { handleLoadAsset, isFinished } = useImageDownloader();

// Step 1 & 2: Define a prop for the texture URL
const props = defineProps({
  animations: {
    type: Array as PropType<ZoneAnimation>,
    required: true,
  },
  show: {
    type: Boolean,
    required: true,
  },
});

const canvas = ref<HTMLCanvasElement | null>(null);
let gl: WebGLRenderingContext | null = null;
let texture: WebGLTexture | null = null;
let program: WebGLProgram | null = null;
let buffers: any = { position: null, texCoord: null };
const textures = new Map();

let lastFrameTime = 0;
const desiredFPS = 20;
const frameInterval = 1000 / desiredFPS;

// Assuming props are already defined with textureUrl
let spriteSheetWidth = ref(0);
let spriteSheetHeight = ref(0);
let numFramesPerRow = 12;
let currentFrame = ref(0);
let totalFrames = ref(0);
let frameWidth = ref(0);
let frameHeight = ref(0);
let currentAnimation = ref(props.animations[0]);
let textureUrl = ref('');
let animationHandler = ref(0);

const webGLError = ref(false);

const urls: { [key: string]: URL } = {};
let observer: MutationObserver = new MutationObserver(() => {});

const getSpriteUrlLq = (name: string): string => {
  // workaround for black recatangle in Firefox for train animation
  if (name === 'anim-id-0-A') {
    return getSpriteUrlHq(name);
  }

  const key = `animation-sprite-${name}-lq`;
  if (!(key in urls)) {
    urls[key] = new URL(
      `/src/assets/images/Animations/Animations_sprites_50/half/${name}.avif`,
      import.meta.url,
    );
  }

  return urls[key].href;
};
const getSpriteUrlHq = (name: string): string => {
  const key = `animation-sprite-${name}-hq`;
  if (canvas.value?.classList.contains('hidden')) {
    return getSpriteUrlLq(name);
  }
  if (!(key in urls)) {
    urls[key] = new URL(
      `/src/assets/images/Animations/Animations_sprites_50/${name}.avif`,
      import.meta.url,
    );
  }

  return urls[key].href;
};
const getSpriteUrl = (name: string): string => {
  if (isHighQuality.value) {
    return getSpriteUrlHq(name);
  } else {
    return getSpriteUrlLq(name);
  }
};
const getFirstUrl = (name: string, type: 'rgb' | 'bw') => {
  const key = `animation-first-${type}-${name}`;
  if (!(key in urls)) {
    urls[key] = new URL(
      `/src/assets/images/Animations/Firsts/${name}-first-${type}.avif?url`,
      import.meta.url,
    );
  }

  return urls[key].href;
};
const firstFrameUrl = computed(() => {
  const type = poiFirstsFrames.value.includes(
    `${props.animations[0].name}-first-rgb` as FirstAnimationFrame<'rgb'>,
  )
    ? 'rgb'
    : 'bw';

  return getFirstUrl(props.animations[0].name, type);
});

watch(isRgb, (newValue) => {
  if (!newValue) {
    // clear animation
    //    gl?.clearColor(0.0, 0.0, 0.0, 1.0); // te 2 linijki czyszczą całą canvę
    //    gl?.clear(gl?.COLOR_BUFFER_BIT);
  } else {
    // start animation
    textureUrl.value = getSpriteUrl(currentAnimation.value.name);
    loadTexture(textureUrl.value);
  }
});

watch(isHighQuality, () => {
  textureUrl.value = getSpriteUrl(currentAnimation.value.name);
  if (animationHandler.value) {
    cancelAnimationFrame(animationHandler.value);
    animationHandler.value = 0;
  }
  webGLError.value = false;
  loadTexture(textureUrl.value);
});

function pauseOrPlayAnimation(trigger: boolean) {
  if (trigger) {
    if (animationHandler.value) {
      cancelAnimationFrame(animationHandler.value);
      animationHandler.value = 0;
    }
  } else {
    loadTexture(textureUrl.value);
  }
}

// improve isDragging watcher to update only when animation is not hidden
watch(isDragging, pauseOrPlayAnimation);

onMounted(() => {
  if (!canvas.value) return;
  gl = canvas.value.getContext('webgl');
  if (!gl) {
    console.warn('Unable to initialize WebGL.');
    webGLError.value = true;
    return;
  }

  observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
        const isHidden = canvas.value?.classList.contains('hidden') || true;
        pauseOrPlayAnimation(isHidden);
      }
    });
  });

  observer.observe(canvas.value, { attributes: true });

  try {
    buffers = initBuffers(gl);
    preloadTextures();
    const vertexShaderSource = `
      attribute vec2 a_position;
      attribute vec2 a_texCoord;
      varying vec2 v_texCoord;
      void main() {
        gl_Position = vec4(a_position, 0.0, 1.0);
        v_texCoord = a_texCoord;
      }
    `;
    const fragmentShaderSource = `
      precision mediump float;
      varying vec2 v_texCoord;
      uniform sampler2D uSampler;
      void main() {
        gl_FragColor = texture2D(uSampler, v_texCoord);
      }
    `;
    const vertexShader = createShader(gl!, gl!.VERTEX_SHADER, vertexShaderSource);
    const fragmentShader = createShader(gl!, gl!.FRAGMENT_SHADER, fragmentShaderSource);
    program = createProgram(gl!, vertexShader, fragmentShader);

    textureUrl.value = getSpriteUrlLq(props.animations[0].name);
    loadTexture(textureUrl.value);
  } catch (error) {
    console.warn('Error during WebGL initialization:', error);
    webGLError.value = true;
  }
});

onBeforeUnmount(() => {
  try {
    observer.disconnect();
    cleanupWebGLResources();
  } catch (error) {
    console.warn('Error during WebGL resource cleanup:', error);
  }
});

function checkMaxTextureSize(gl: WebGLRenderingContext, width: number, height: number): boolean {
  const maxTextureSize = gl.getParameter(gl.MAX_TEXTURE_SIZE);
  return width <= maxTextureSize && height <= maxTextureSize;
}

function initBuffers(gl: WebGLRenderingContext) {
  const positionBuffer = gl!.createBuffer();
  gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer);

  const positions = [-1.0, 1.0, 1.0, 1.0, -1.0, -1.0, 1.0, -1.0];
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(positions), gl.STATIC_DRAW);

  const texCoordBuffer = gl!.createBuffer();
  gl.bindBuffer(gl.ARRAY_BUFFER, texCoordBuffer);

  if (!(texCoordBuffer instanceof WebGLBuffer) || !(positionBuffer instanceof WebGLBuffer)) {
    return false;
  }

  return {
    position: positionBuffer,
    texCoord: texCoordBuffer,
  };
}

function cleanupWebGLResources() {
  if (!gl) return;

  textures.forEach((texture) => {
    gl!.deleteTexture(texture);
  });
  textures.clear();

  // Delete buffers
  if (buffers.position) gl.deleteBuffer(buffers.position);
  if (buffers.texCoord) gl.deleteBuffer(buffers.texCoord);
  buffers = false;

  // Delete shaders and program
  if (program) {
    const shaders = gl.getAttachedShaders(program);
    shaders!.forEach((shader) => {
      gl!.deleteShader(shader);
    });
    gl.deleteProgram(program);
    program = null;
  }
}

async function preloadTextures() {
  const textureNames = props.animations.map((a) => a.name);
  for (const name of textureNames) {
    const url = getSpriteUrl(name);
    try {
      const texture = loadTexture(url);
      textures.set(name, texture);
    } catch (error) {
      console.warn('Error during texture preload:', error);
    }
  }
}

function loadTexture(url: string) {
  if (textures.has(url)) {
    Promise.resolve(textures.get(url));
  }
  const image = new Image();
  const resolvedUrl = new URL(textureUrl.value, import.meta.url).href;
  image.onload = () => {
    const isValidSize = checkMaxTextureSize(gl!, image.width, image.height);
    if (!isValidSize) {
      console.warn(
        `Texture size exceeds the maximum allowed size of ${gl!.getParameter(gl!.MAX_TEXTURE_SIZE)}.`,
      );
      webGLError.value = true;
      return;
    }
    spriteSheetWidth.value = image.width;
    spriteSheetHeight.value = image.height;
    frameWidth.value = image.width / 12;
    frameHeight.value = image.height / currentAnimation.value.duration;
    totalFrames.value = currentAnimation.value.frames;

    if (!gl || !(gl instanceof WebGLRenderingContext)) {
      console.warn('Failed to get Webgl context');
      webGLError.value = true;
      return;
    }

    texture = gl?.createTexture() ?? null;
    gl?.bindTexture(gl.TEXTURE_2D, texture);
    gl?.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, true);
    gl?.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, image);
    if (isPowerOf2(image.width) && isPowerOf2(image.height)) {
      gl?.generateMipmap(gl.TEXTURE_2D);
    } else {
      gl?.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
      gl?.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
      gl?.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    }

    if (!program) {
      console.warn('Failed to create WebGL program');
      webGLError.value = true;
      return;
    }

    const attribLocations = {
      vertexPosition: gl!.getAttribLocation(program, 'a_position'),
      textureCoord: gl!.getAttribLocation(program, 'a_texCoord'),
    };

    const uniformLocations = {
      uSampler: gl!.getUniformLocation(program, 'uSampler'),
    };

    if (!buffers) {
      console.warn('Failed to initialize buffers');
      webGLError.value = true;
      return;
    }
    drawScene(gl, program, buffers, texture, attribLocations, uniformLocations, 0);
  };
  image.src = resolvedUrl;
  return image;
}

// Fix for WebGL
function isPowerOf2(value: number): boolean {
  return (value & (value - 1)) === 0;
}

function createShader(gl: WebGLRenderingContext, type: GLenum, source: string): WebGLShader | null {
  const shader = gl.createShader(type);
  if (!shader) {
    console.warn('Unable to create shader');
    webGLError.value = true;
    return null;
  }
  gl.shaderSource(shader, source);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    console.warn('An error occurred compiling the shaders: ' + gl.getShaderInfoLog(shader));
    gl.deleteShader(shader);
    webGLError.value = true;
    return null;
  }
  return shader;
}

function createProgram(
  gl: WebGLRenderingContext,
  vertexShader: WebGLShader | null,
  fragmentShader: WebGLShader | null,
): WebGLProgram | null {
  if (!vertexShader || !fragmentShader) return null;
  const program = gl.createProgram();
  if (!program) {
    console.warn('Unable to create program');
    webGLError.value = true;
    return null;
  }
  gl.attachShader(program, vertexShader);
  gl.attachShader(program, fragmentShader);
  gl.linkProgram(program);
  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
    console.warn('Unable to initialize the shader program: ' + gl.getProgramInfoLog(program));
    webGLError.value = true;
    return null;
  }
  return program;
}

function drawScene(
  gl: WebGLRenderingContext,
  program: WebGLProgram,
  buffers: { position: WebGLBuffer; texCoord: WebGLBuffer },
  texture: WebGLTexture | null,
  attribLocations: { vertexPosition: number; textureCoord: number },
  uniformLocations: { uSampler: WebGLUniformLocation | null },
  timestamp: DOMHighResTimeStamp,
) {
  const elapsed = timestamp - lastFrameTime;
  if (elapsed > frameInterval) {
    lastFrameTime = timestamp - (elapsed % frameInterval);

    gl.clearColor(0.0, 0.0, 0.0, 1.0);
    gl.clear(gl.COLOR_BUFFER_BIT);

    // Tell WebGL to use our program when drawing
    gl.useProgram(program);

    // Turn on the attribute
    gl.enableVertexAttribArray(attribLocations.vertexPosition);
    gl.bindBuffer(gl.ARRAY_BUFFER, buffers.position);
    gl.vertexAttribPointer(attribLocations.vertexPosition, 2, gl.FLOAT, false, 0, 0);

    // Turn on the texture coordinate attribute
    gl.enableVertexAttribArray(attribLocations.textureCoord);
    gl.bindBuffer(gl.ARRAY_BUFFER, buffers.texCoord);

    // Calculate the texture coordinates for the current frame
    const animationFrame = clamp(
      currentAnimation.value.startingFrame,
      currentFrame.value,
      currentAnimation.value.endingFrame,
    );
    const frameIndex = animationFrame - currentAnimation.value.startingFrame;

    const frameX = frameIndex % numFramesPerRow;
    const frameY = Math.floor(frameIndex / numFramesPerRow);

    const texCoordX = (frameX * frameWidth.value) / spriteSheetWidth.value;
    const texCoordXNext = texCoordX + frameWidth.value / spriteSheetWidth.value;
    const texCoordY = (frameY * frameHeight.value) / spriteSheetHeight.value;
    const texCoordYNext = texCoordY + frameHeight.value / spriteSheetHeight.value;

    const textureCoordinates = [
      texCoordX,
      texCoordY,
      texCoordXNext,
      texCoordY,
      texCoordX,
      texCoordYNext,
      texCoordXNext,
      texCoordYNext,
    ];

    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(textureCoordinates), gl.STATIC_DRAW);
    gl.vertexAttribPointer(attribLocations.textureCoord, 2, gl.FLOAT, false, 0, 0);

    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, texture);
    gl.uniform1i(uniformLocations.uSampler, 0);

    const offset = 0;
    const vertexCount = 4;
    gl.drawArrays(gl.TRIANGLE_STRIP, offset, vertexCount);

    currentFrame.value = (currentFrame.value + 1) % 360; //360 - total frames

    if (props.animations?.length > 1) {
      const nextAnimationIndex: number =
        currentFrame.value >= props.animations[1].startingFrame ? 1 : 0;

      // switch animation
      if (props.animations[nextAnimationIndex].name != currentAnimation.value.name) {
        currentAnimation.value = props.animations[nextAnimationIndex];
        textureUrl.value = getSpriteUrl(props.animations[nextAnimationIndex].name);
        cancelAnimationFrame(animationHandler.value);
        animationHandler.value = 0;
        loadTexture(textureUrl.value);
        return;
      }
    }
  }

  if (isRgb.value && !isDragging.value) {
    animationHandler.value = requestAnimationFrame((newTimestamp) => {
      drawScene(gl, program, buffers, texture, attribLocations, uniformLocations, newTimestamp);
    });
  }
}
</script>

<template>
  <div data-img-preload v-if="!isFinished">
    <template v-for="(animation, index) in props.animations" :key="index">
      <img
        :src="getSpriteUrlHq(animation.name)"
        class="absolute z-rgb-animations hidden"
        @load="handleLoadAsset"
      />
      <img
        :src="getSpriteUrlLq(animation.name)"
        class="absolute z-rgb-animations hidden"
        @load="handleLoadAsset"
      />
      <template v-if="index === 0">
        <img
          :src="getFirstUrl(animation.name, 'bw')"
          class="absolute z-rgb-animations hidden"
          @load="handleLoadAsset"
        />
        <img
          :src="getFirstUrl(animation.name, 'rgb')"
          class="absolute z-rgb-animations hidden"
          @load="handleLoadAsset"
        />
      </template>
    </template>
  </div>
  <img
    v-if="!isRgb || webGLError || !props.show"
    :src="firstFrameUrl"
    :alt="currentAnimation.name"
    :style="{
      width: `${currentAnimation.width}px`,
      height: `${currentAnimation.height}px`,
    }"
    :data-name="currentAnimation.name"
    class="absolute z-rgb-animations max-w-none will-change-transform"
  />
  <canvas
    v-show="isRgb && !webGLError"
    v-if="props.show"
    :id="currentAnimation.name"
    ref="canvas"
    :width="currentAnimation.width"
    :height="currentAnimation.height"
  ></canvas>
</template>
